import { ChangeEvent, FC, memo, useCallback, useEffect, useReducer } from 'react';

import {
  TwoFactorAuthentication,
  TwoFactorAuthenticationType,
} from '@/legacy/features/user/twoFactorAuthentication/twoFactorAuthentication';

import { SmsCountry } from './helper';
import {
  ActionType,
  getInitialState,
  handleQueries,
  reducer,
  State,
} from './TwoFactorSetupDialog.reducer';
import TwoFactorSetupDialogContents from './TwoFactorSetupDialogContents';

interface Props {
  isOpen: boolean;
  onClose: { (isSuccess: boolean): void };

  /**
   * If passed in identifies which TwoFactorAuthentication we're updating
   * If not specified user will be adding a new authentication method
   */
  twoFactorAuthentication?: TwoFactorAuthentication;
  /**
   * If passed, it can be used to find out any information about currently set up
   * two factor authentications for current user.
   */
  twoFactorAuthentications?: Array<TwoFactorAuthentication>;
}

const TwoFactorSetupDialog: FC<Props> = (props: Props): React.JSX.Element => {
  const { isOpen, onClose, twoFactorAuthentication, twoFactorAuthentications } = props;

  const [state, dispatch] = useReducer(
    reducer,
    getInitialState({
      twoFactorAuthentication,
      twoFactorAuthentications,
    })
  );

  // We'll pass down state without queries so we don't re-render if queries change
  const { queries, ...stateWithoutQueries } = state;

  useEffect(
    () => {
      handleQueries(queries, dispatch, onClose);
    },
    // Intentionally only run this effect when state.queries changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queries]
  );

  // @todo move this business logic into the reducer
  useEffect(() => {
    dispatch({ type: ActionType.SetSteps });
  }, [state.isEnabled, state.verificationMethod]);

  const handleSmsCountryChange = useCallback(
    (e) => dispatch({ type: ActionType.SetFormCountry, payload: e.target.value as SmsCountry }),
    []
  );

  const handlePhoneNumberChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
      dispatch({
        type: ActionType.SetFormPhoneNumber,
        payload: e.target.value as string,
      }),
    []
  );

  const handleUnableToScan = useCallback(() => dispatch({ type: ActionType.UnableToScan }), []);

  const handleVerificationChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      dispatch({
        type: ActionType.SetFormVerificationMethod,
        payload: e.target.value as TwoFactorAuthenticationType,
      }),
    []
  );

  const handleNextStep = useCallback(() => dispatch({ type: ActionType.NextStep }), []);

  const handlePreviousStep = useCallback(() => dispatch({ type: ActionType.PreviousStep }), []);

  const handleCodeChange = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
      dispatch({
        type: ActionType.SetCode,
        payload: e.target.value as string,
      }),
    []
  );

  const handleSubmit = useCallback(() => {
    dispatch({ type: ActionType.NextStep });
  }, []);

  const handleClose = () => {
    dispatch({ type: ActionType.CloseDialog });
    onClose(false);
  };

  const handleDisableVerificationChange = useCallback((verification: TwoFactorAuthentication) => {
    if (!verification) {
      dispatch({
        type: ActionType.ShowFieldError,
        payload: {
          fieldName: 'disableVerification',
          errorMessage: "Please select a verification method to verify it's really you.",
        },
      });
    } else {
      dispatch({
        type: ActionType.SetDisableVerificationMethod,
        payload: verification,
      });
    }
  }, []);

  const handleResendCode = useCallback(() => dispatch({ type: ActionType.ResendSmsCode }), []);

  const handleCallMe = useCallback(() => dispatch({ type: ActionType.CallUser }), []);

  return (
    <TwoFactorSetupDialogContents
      twoFactorAuthenticationDisabler={state.twoFactorAuthenticationDisabler}
      twoFactorAuthenticationDisablers={state.twoFactorAuthentications}
      isOpen={isOpen}
      onClose={handleClose}
      onCodeChange={handleCodeChange}
      onNextStep={handleNextStep}
      onPhoneNumberChange={handlePhoneNumberChange}
      onPreviousStep={handlePreviousStep}
      onSmsCountryChange={handleSmsCountryChange}
      onSubmit={handleSubmit}
      onUnableToScan={handleUnableToScan}
      onVerificationChange={handleVerificationChange}
      onTwoFactorAuthenticationDisablerChange={handleDisableVerificationChange}
      onResendCode={handleResendCode}
      onCallMe={handleCallMe}
      state={stateWithoutQueries as State}
    />
  );
};

export default memo(TwoFactorSetupDialog);
